import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";
import type { CustomFieldAssociatedType } from "@btnet/services-btnet/handlers/models/customFieldAssociatedType";
import type { MultiLineTextCustomFieldDefinition } from "@btnet/services-btnet/handlers/models/multiLineTextCustomFieldDefinition";
import type { MultiLineTextCustomFieldValue } from "@btnet/services-btnet/handlers/models/multiLineTextCustomFieldValue";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";
import { type ICustomFieldTypeHandler } from "commonComponents/entity/customField/utils/customFieldValues.util";
import { TextTruncateMultiline } from "commonComponents/utilities/TextTruncate/TextTruncateMultiline";

import { MultiLineTextCellEditor } from "./MultiLineTextCellEditor";

export const MultiLineTextCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    MultiLineTextCustomFieldValue,
    MultiLineTextCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.value || "";
    },

    setValue(field, value): MultiLineTextCustomFieldValue {
        return { ...field, value: value ?? "" };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): MultiLineTextCustomFieldValue {
        return {
            dataType: "MultiLineText",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? "",
        };
    },

    compare(valueA: string | null, valueB: string | null): number {
        const a = valueA ?? "";
        const b = valueB ?? "";
        return a.localeCompare(b, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null): ReactNode {
        const trimmedValue = value?.trim();
        if (!trimmedValue) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }

        return (
            <TextTruncateMultiline
                lines={10}
                style={{ whiteSpace: "pre-line", lineHeight: 1.5715 }}
            >
                {trimmedValue}
            </TextTruncateMultiline>
        );
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string) => void,
        onBlur: () => void,
        autoFocus?: boolean,
    ): ReactNode {
        return (
            <BTTextArea
                id={`multiLineTextCustomFieldEditor-${customFieldId}`}
                data-testid="multiLineTextCustomFieldEditor"
                value={value ?? ""}
                readOnly={false}
                onChange={(_id, value) => onChange(value)}
                onBlur={(_id, _touched, blurValue) => {
                    if (blurValue !== undefined) {
                        onChange(blurValue);
                    }
                    onBlur();
                }}
                placeholder={friendlyDefaultDisplayValue}
                formMode="click-to-edit"
                blurOnChange={false}
                minRows={1}
                maxRows={10}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
            />
        );
    },

    getAgGridCellEditor() {
        return MultiLineTextCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return context.displayContent;
    },
};
